import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Switch from 'react-switch';

import Label from '../../../../../Label';
import MultiSelect from '../../../../../MultiSelect';
import { PLACEHOLDER_LABEL_MAP, NAME_PLACEHOLDER_KEY } from './constants';
import {
  CARD_ELEMENT_TYPE,
  PHOTO_TYPE,
} from '../../../../../../../services/cards/constants';
import {
  FONT_FAMILY,
  googleFontInfo,
  getCardElementTextFontFamily,
} from '../../../CardDesignerCardElements/CardDesignerCardElementText/helpers';
import { capitalize } from '../../../../../../../util/text';
import FontPreload from '../../../CardDesignerCardElements/CardDesignerCardElementText/components/FontPreload';

const propTypes = {
  className: PropTypes.string,
  elements: PropTypes.array,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerIDCardElementControls = ({
  className,
  elements = [],
  onElementConfigurationChange,
}) => {
  const classes = classNames({
    'card-designer-id-card-element-controls': true,
    [className]: !!className,
  });

  const normalizePlaceholder = element =>
    (element?.cardElementConfiguration?.help_placeholder ?? '')
      .toString()
      .trim()
      .toLowerCase();

  const isNameTextElement = element =>
    normalizePlaceholder(element) === NAME_PLACEHOLDER_KEY;

  const getElementLabel = (element, fallback) => {
    const placeholder = normalizePlaceholder(element);
    if (PLACEHOLDER_LABEL_MAP[placeholder]) {
      return PLACEHOLDER_LABEL_MAP[placeholder];
    }
    const raw = element?.cardElementConfiguration?.help_placeholder;
    if (raw) return raw;
    return fallback;
  };

  const isOn = element => (element?.cardElementConfiguration?.opacity ?? 1) > 0;

  const { barcodeElement, textElements } = useMemo(() => {
    const photoEl = elements.find(
      ({ cardElement, cardElementConfiguration }) =>
        cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        cardElementConfiguration?.photo_type !== PHOTO_TYPE.BACKGROUND &&
        cardElementConfiguration?.photo_type !== PHOTO_TYPE.FOREGROUND &&
        cardElementConfiguration?.photo_type !== PHOTO_TYPE.LOGO,
    );
    const backgroundEl = elements.find(
      ({ cardElementConfiguration }) =>
        cardElementConfiguration?.photo_type === PHOTO_TYPE.BACKGROUND,
    );
    const barcodeEl = elements.find(
      ({ cardElement }) => cardElement.type === CARD_ELEMENT_TYPE.BARCODE,
    );
    const textEls = elements
      .filter(({ cardElement }) => cardElement.type === CARD_ELEMENT_TYPE.TEXT)
      .reverse();

    return {
      photoElement: photoEl,
      backgroundElement: backgroundEl,
      barcodeElement: barcodeEl,
      textElements: textEls,
    };
  }, [elements]);

  const handleToggle = element => {
    const next = isOn(element) ? 0 : 1;
    onElementConfigurationChange({
      element,
      elementConfig: {
        opacity: next,
      },
    });
  };

  // Determine shared font family from text elements (use first non-empty)
  const sharedFontFamily = useMemo(() => {
    const firstWithFont = textElements.find(
      el => el?.cardElementConfiguration?.font_family,
    );
    return firstWithFont
      ? firstWithFont.cardElementConfiguration.font_family
      : null;
  }, [textElements]);

  const fontOptions = useMemo(
    () =>
      Object.values(FONT_FAMILY).map(font => {
        const fontInfo = googleFontInfo.find(f => f.font === font);
        return {
          value: font,
          label: font
            .replace('GOOGLE_', '')
            .split('_')
            .map(s => capitalize(s))
            .join(' '),
          fontFamily: fontInfo?.fontFamily,
        };
      }),
    [],
  );

  const selectedFontOption = useMemo(() => {
    if (!sharedFontFamily) return null;
    return fontOptions.find(opt => opt.value === sharedFontFamily) ?? null;
  }, [sharedFontFamily, fontOptions]);

  const handleFontChange = option => {
    const value = option ? option.value : null;
    textElements.forEach(element => {
      onElementConfigurationChange({
        element,
        elementConfig: {
          font_family: value,
        },
      });
    });
  };

  const renderRow = (key, label, element, { disabled = false } = {}) => {
    if (!element) return null;
    return (
      <div
        key={key}
        className="card-designer-id-card-element-controls-row d-flex align-items-center justify-content-between mb-2"
      >
        <span>
          {label}
          {disabled && <span className="text-danger ml-1">*</span>}
        </span>
        <Switch
          uncheckedIcon={false}
          checkedIcon={false}
          height={20}
          width={40}
          onColor="#7ace21"
          offColor="#cdd6e4"
          checked={isOn(element)}
          disabled={disabled}
          onChange={() => {
            if (disabled) return;
            handleToggle(element);
          }}
        />
      </div>
    );
  };

  return (
    <div className={classes}>
      <Label id="id-card-element-controls">Member Data</Label>
      <p className="text-light text-xs mb-2">
        These data fields will be dynamically populated with member information
        when ID cards are generated.
      </p>

      {textElements.map((element, idx) => {
        const disabled = isNameTextElement(element);
        return renderRow(
          `text-${element.cardElement.id}`,
          getElementLabel(element, `Text ${idx + 1}`),
          element,
          { disabled },
        );
      })}
      {renderRow(
        'barcode',
        getElementLabel(barcodeElement, 'Barcode'),
        barcodeElement,
      )}

      {textElements.length > 0 && (
        <div className="mt-3">
          <Label id="id-card-font">Font</Label>
          <MultiSelect
            id="id-card-font-family"
            className="card-designer-font-family-selector"
            placeholder="Select font"
            options={fontOptions}
            value={selectedFontOption}
            onChange={handleFontChange}
            formatOptionLabel={option => (
              <span style={{ fontFamily: option.fontFamily }}>
                {option.label}
              </span>
            )}
            styles={{
              fontFamily: sharedFontFamily
                ? getCardElementTextFontFamily({
                    font_family: sharedFontFamily,
                  })
                : undefined,
            }}
          />
          <FontPreload />
        </div>
      )}
    </div>
  );
};

CardDesignerIDCardElementControls.propTypes = propTypes;

export default CardDesignerIDCardElementControls;
